import React, { useEffect, useState } from "react";
import { Menu } from "@mui/material";
import { LuCalendarCheck } from "react-icons/lu";

import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";

import VuiBox from "components/VuiBox";
import VuiButton from "components/VuiButton";
import VuiInput from "components/VuiInput";
import VuiTypography from "components/VuiTypography";

const formatLabel = (start, end) => {
  if (!start || !end) return "Date Range";
  return `${start} → ${end}`;
};

const DateRangeButton = ({ value = ["", ""], onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [draftRange, setDraftRange] = useState(value);
  const { dark, light, primary } = colors;

  const open = Boolean(anchorEl);
  const [startDate, endDate] = draftRange;

  useEffect(() => {
    setDraftRange(value || ["", ""]);
  }, [value]);

  const handleOpen = (event) => {
    setDraftRange(value || ["", ""]);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleStartChange = (e) => {
    const nextStart = e.target.value;
    let nextEnd = endDate;

    if (nextEnd && nextStart && nextEnd < nextStart) {
      nextEnd = "";
    }

    setDraftRange([nextStart, nextEnd]);
  };

  const handleEndChange = (e) => {
    setDraftRange([startDate, e.target.value]);
  };

  const handleReset = () => {
    const cleared = ["", ""];
    setDraftRange(cleared);
    onChange?.(cleared);
    handleClose();
  };

  const handleApply = () => {
    onChange?.(draftRange);
    handleClose();
  };

  const isApplyDisabled = !startDate || !endDate;

  return (
    <>
      <VuiButton
        variant="outlined"
        size="small"
        color="primary"
        startIcon={<LuCalendarCheck size={14} />}
        onClick={handleOpen}
        sx={{
          height: 38,
          padding: "0 20px",
          fontWeight: 400,
          fontSize: "0.875rem",
          textTransform: "none",
          color: "primary",
          justifyContent: "flex-start",
          border: `1.3px solid ${rgba(dark.main, 0.5)}`,
          borderRadius: "14px",
          background: "transparent",
          "&:hover": {
            background: "rgba(255,255,255,0.04)",
            border: `1.3px solid ${rgba(dark.main, 0.5)}`,
          },
          "& .MuiButton-startIcon": {
            marginRight: "8px",
          },
        }}
      >
        {formatLabel(value?.[0], value?.[1])}
      </VuiButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 2,
            minWidth: 250,
            background: "rgba(255, 255, 255, 0.06)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            border: "1px solid rgba(255, 255, 255, 0.18)",
            borderRadius: "16px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.35)",
            color: "#fff",
          },
        }}
      >
        <VuiTypography variant="caption" mb={1}>
          Apply date range
        </VuiTypography>

        <VuiBox display="flex" gap={1.5}>
          <VuiInput
            gradient
            type="date"
            value={startDate}
            onChange={handleStartChange}
            fullWidth
            inputProps={{
              max: endDate || undefined,
            }}
          />

          <VuiInput
            gradient
            type="date"
            value={endDate}
            onChange={handleEndChange}
            fullWidth
            inputProps={{
              min: startDate || undefined,
            }}
          />
        </VuiBox>

        <VuiBox mt={2} display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
          <VuiButton
            size="small"
            variant="text"
            disabled={!startDate && !endDate}
            onClick={handleReset}
            sx={{
              fontSize: "0.75rem",
              opacity: !startDate && !endDate ? 0.4 : 0.8,
              "&:hover": {
                opacity: 1,
                background: "rgba(255,255,255,0.08)",
              },
            }}
          >
            Reset
          </VuiButton>

          <VuiButton
            size="small"
            color="primary"
            variant="contained"
            onClick={handleApply}
            disabled={isApplyDisabled}
            sx={{
              opacity: isApplyDisabled ? 0.6 : 1,
            }}
          >
            Apply
          </VuiButton>
        </VuiBox>
      </Menu>
    </>
  );
};

export default DateRangeButton;
